import React, { useState, useRef, useEffect } from "react";
import "./AddVersionModal.css";
import uploadIcon from "../../assets/upload.svg";
import { OrbitProgress } from "react-loading-indicators";
import useOrganizationLimits from "../../hooks/useOrganizationLimits";
import { useFileDrop } from "../../hooks/useFileDrop";
import toast from "react-hot-toast";
import CloseButton from "../CloseButton/CloseButton";
import { useNavigate } from "react-router-dom";

const AddVersionModal = ({
  onClose,
  onSubmit,
  isVersionLoading,
  currentMegabytes = 0,
  totalMegabytes = 0,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const limits = useOrganizationLimits();
  const navigate = useNavigate();
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const modalRef = useRef(null);
  const fileInputRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const processFile = (file) => {
    if (!limits) {
      navigate("/pricing");
      return;
    }
    const sizeLimit =
      limits &&
      limits.find((value) => value.attributeCode === "AVAILABLE_SPACE_MB");
    if (sizeLimit) {
      const availableSpace =
        (sizeLimit.maxValue - sizeLimit.usedValue) * 1024 * 1024;

      if (file && availableSpace < file.size) {
        toast.error(
          `Недостаточно места в соответствии с вашим текущим тарифом. Доступно: ${(
            sizeLimit.maxValue - sizeLimit.usedValue
          ).toFixed(2)}МБ`
        );
        return;
      }
    }

    if (file) {
      setSelectedFile(file);
      generateThumbnail(file);
    }
  };

  const handleFileSelect = (e) => processFile(e.target.files[0]);

  const { isDragActive, dropHandlers } = useFileDrop({
    onFile: processFile,
    disabled: isVersionLoading,
  });

  const handleClearFile = () => {
    setSelectedFile(null);
    setThumbnailUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const generateThumbnail = (file) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = URL.createObjectURL(file);
    video.muted = true;
    video.playsInline = true;
    video.currentTime = 1;
    video.onloadeddata = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      setThumbnailUrl(canvas.toDataURL("image/png"));
      URL.revokeObjectURL(video.src);
    };
  };

  const handleSubmit = () => {
    if (isVersionLoading) return;
    if (selectedFile) {
      onSubmit(selectedFile);
    }
  };

  const renderUploadingState = () => {
    // Calculate progress (0 to 1)
    const progress =
      totalMegabytes && currentMegabytes
        ? Math.min(currentMegabytes / totalMegabytes, 1)
        : 0;

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 16,
        }}
      >
        {/* Progress bar background */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${progress * 100}%`,
            background: "#333B4B",
            transition: "width 0.3s",
            zIndex: 1,
          }}
        />
        {/* Overlay content */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <OrbitProgress style={{ fontSize: 8 }} color="#3496FF" />
          <span style={{ color: "#C1C4CB", fontSize: 16, marginTop: 5 }}>
            Загружается
          </span>
          {!!totalMegabytes ? (
            <span style={{ color: "#717B8E", fontSize: 14, marginTop: 5 }}>
              {(currentMegabytes / 1024 / 1024).toFixed(2)}МБ /{" "}
              {(totalMegabytes / 1024 / 1024).toFixed(2)}МБ
            </span>
          ) : (
            <span style={{ color: "#717B8E", fontSize: 14, marginTop: 5 }}>
              Подготовка...
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="add-version-modal" ref={modalRef}>
        <CloseButton onClose={onClose} />
        <h1 className="add-version-modal-title">Загрузить видео</h1>
        <div className="add-version-modal-content">
          <label
            className={`add-version-upload-area${isDragActive ? " drag-active" : ""}`}
            style={{ width: 400, height: 144, marginBottom: 32 }}
            {...dropHandlers}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            {isVersionLoading ? (
              renderUploadingState()
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt="Video thumbnail"
                className="add-version-poster-image"
                style={{ borderRadius: 16 }}
              />
            ) : (
              <>
                <img
                  src={uploadIcon}
                  alt="Video thumbnail"
                  style={{ width: 32 }}
                />
                <div className="add-version-upload-text">
                  Выберите видео или перетащите файл сюда
                </div>
                {/* <div className="add-version-upload-limit">до 20 МБ</div> */}
              </>
            )}
          </label>
        </div>
        <button
          className="add-version-submit-button"
          onClick={handleSubmit}
          disabled={!selectedFile || isVersionLoading}
        >
          {isVersionLoading ? "Загрузка..." : "Загрузить"}
        </button>
      </div>
    </div>
  );
};

export default AddVersionModal;
